import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";
import { BUTTON_BASE_CLASSES, BUTTON_DISABLED_WITH_BORDER_CLASSES } from "./helpers";

import { Tooltip } from "mds/components/Tooltip";
import type { PolymorphicRef } from "types";

export type RoundedIconButtonProps<T extends React.ElementType = "button"> = BaseButtonProps<T> & {
  // Icon buttons are required to have an aria-label. This label will also be used for the tooltip content.
  "aria-label": string;
};

export const RoundedIconButton = forwardRef<
  HTMLButtonElement,
  Omit<RoundedIconButtonProps<"button">, "ref">
>(function RoundedIconButtonInternal(
  { className, "aria-label": ariaLabel, ...rest }: RoundedIconButtonProps<"button">,
  ref,
) {
  return (
    <Tooltip content={ariaLabel}>
      <BaseButton
        aria-label={ariaLabel}
        className={twMerge(
          BUTTON_BASE_CLASSES,
          BUTTON_DISABLED_WITH_BORDER_CLASSES,
          "h-[44px] w-[44px] rounded-full border border-black-tint-90 bg-black-tint-99 text-black-tint-20",
          "hover:bg-black-tint-97",
          className,
        )}
        ref={ref}
        {...rest}
      />
    </Tooltip>
  );
}) as <T extends React.ElementType = "button">(
  props: RoundedIconButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
